/**
 * Formatting Utilities
 * Provides functions for formatting various data types for display
 */

/**
 * Format file size in bytes to human-readable format
 * 
 * @param {number} bytes - File size in bytes
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted file size (e.g., "15.00 MB")
 * 
 * @example
 * formatFileSize(1024) // returns "1.00 KB"
 * formatFileSize(15728640) // returns "15.00 MB"
 * formatFileSize(0) // returns "0 Bytes"
 */
export const formatFileSize = (bytes, decimals = 2) => {
  if (!bytes || bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

/**
 * Format date to readable string
 * 
 * @param {string|Date} date - Date to format
 * @param {string} format - Format type ('short', 'long', 'relative', 'iso')
 * @returns {string} Formatted date string
 * 
 * @example
 * formatDate("2024-01-15", "short") // returns "Jan 15, 2024"
 * formatDate("2024-01-15", "long") // returns "January 15, 2024"
 * formatDate("2024-01-15", "relative") // returns "2 days ago" (relative to now)
 */
export const formatDate = (date, format = 'short') => {
  if (!date) return 'N/A';
  
  const dateObj = new Date(date);
  
  if (isNaN(dateObj.getTime())) return 'Invalid Date';
  
  switch (format) {
    case 'short':
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
      
    case 'long':
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      
    case 'relative':
      return formatRelativeTime(dateObj);
      
    case 'iso':
      return dateObj.toISOString();
      
    case 'datetime':
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      
    default:
      return dateObj.toLocaleDateString();
  }
};

/**
 * Format relative time (e.g., "2 days ago")
 * 
 * @param {Date} date - Date to format
 * @returns {string} Relative time string
 */
const formatRelativeTime = (date) => {
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const diffWeek = Math.floor(diffDay / 7);
  const diffMonth = Math.floor(diffDay / 30);
  const diffYear = Math.floor(diffDay / 365);
  
  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
  if (diffWeek < 4) return `${diffWeek} week${diffWeek !== 1 ? 's' : ''} ago`;
  if (diffMonth < 12) return `${diffMonth} month${diffMonth !== 1 ? 's' : ''} ago`;
  return `${diffYear} year${diffYear !== 1 ? 's' : ''} ago`;
};

/**
 * Format number with thousand separators
 * 
 * @param {number} num - Number to format
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} Formatted number string
 * 
 * @example
 * formatNumber(1234567) // returns "1,234,567"
 * formatNumber(1234.56, 2) // returns "1,234.56"
 */
export const formatNumber = (num, decimals = 0) => {
  if (num === null || num === undefined) return '0';
  
  const numValue = parseFloat(num);
  
  if (isNaN(numValue)) return '0';
  
  return numValue.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

/**
 * Format download count with abbreviations for large numbers
 * 
 * @param {number} count - Download count
 * @returns {string} Formatted download count (e.g., "1.2K", "3.5M")
 * 
 * @example
 * formatDownloadCount(500) // returns "500"
 * formatDownloadCount(1500) // returns "1.5K"
 * formatDownloadCount(1500000) // returns "1.5M"
 * formatDownloadCount(2500000000) // returns "2.5B"
 */
export const formatDownloadCount = (count) => {
  if (!count || count === 0) return '0';
  
  const num = parseInt(count, 10);
  
  if (isNaN(num)) return '0';
  
  // Less than 1,000 - show exact number
  if (num < 1000) {
    return num.toString();
  }
  
  // 1,000 - 999,999 - show in K
  if (num < 1000000) {
    const k = num / 1000;
    return k % 1 === 0 ? `${k}K` : `${k.toFixed(1)}K`;
  }
  
  // 1,000,000 - 999,999,999 - show in M
  if (num < 1000000000) {
    const m = num / 1000000;
    return m % 1 === 0 ? `${m}M` : `${m.toFixed(1)}M`;
  }
  
  // 1,000,000,000+ - show in B
  const b = num / 1000000000;
  return b % 1 === 0 ? `${b}B` : `${b.toFixed(1)}B`;
};

/**
 * Format price with currency symbol
 * 
 * @param {number} price - Price value
 * @param {string} currency - Currency code (default: 'USD')
 * @returns {string} Formatted price string
 * 
 * @example
 * formatPrice(9.99) // returns "$9.99"
 * formatPrice(0) // returns "Free"
 */
export const formatPrice = (price, currency = 'USD') => {
  if (!price || price === 0) return 'Free';
  
  const priceNum = parseFloat(price);
  
  if (isNaN(priceNum)) return 'Free';
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(priceNum);
};

/**
 * Format percentage
 * 
 * @param {number} value - Value to format as percentage
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} Formatted percentage string
 * 
 * @example
 * formatPercentage(0.75) // returns "75%"
 * formatPercentage(0.333, 1) // returns "33.3%"
 */
export const formatPercentage = (value, decimals = 0) => {
  if (value === null || value === undefined) return '0%';
  
  const numValue = parseFloat(value);
  
  if (isNaN(numValue)) return '0%';
  
  const percentage = numValue * 100;
  
  return `${percentage.toFixed(decimals)}%`;
};

/**
 * Truncate text with ellipsis
 * 
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} Truncated text
 * 
 * @example
 * truncateText("This is a long text", 10) // returns "This is a..."
 */
export const truncateText = (text, maxLength) => {
  if (!text) return '';
  
  if (text.length <= maxLength) return text;
  
  return text.substring(0, maxLength) + '...';
};
